import { StateCreator } from 'zustand';
import { AdvancedFilterData } from 'components/filter/advancedFilter/FilterFormController';
import { PartialFilterData, UserFilterSet } from 'models/filterSet';
import { arePartialFiltersEqual, convertDateStringsToObjects } from 'utils/filterDefaults';
import { FilterSetMatcher } from 'utils/filterSetMatcher';

export interface FilterSetsState {
  filterSets: UserFilterSet[];
  selectedFilterSet: string | null;
  lastClickedFilterSet: string | null;
}

export interface FilterSetsActions {
  setFilterSets: (filterSets: UserFilterSet[]) => void;
  addFilterSet: (filterSet: UserFilterSet) => void;
  updateFilterSet: (id: string, filterSet: UserFilterSet) => void;
  removeFilterSet: (id: string) => void;
  getFilterSet: (id: string) => UserFilterSet | undefined;
  validateFilterSet: (
    name: string,
    filterValues: AdvancedFilterData,
    excludeId?: string
  ) => { valid: boolean; error?: string };
  setSelectedFilterSet: (id: string | null) => void;
  setLastClickedFilterSet: (id: string | null) => void;
}

export interface FilterSetsSlice extends FilterSetsState, FilterSetsActions {}

export function convertApiFilterSetToUsable(apiSet: UserFilterSet): UserFilterSet {
  let content: PartialFilterData;

  try {
    content = typeof apiSet.content === 'string' ? (JSON.parse(apiSet.content) as PartialFilterData) : apiSet.content;

    if (content.selectedDateRange) {
      content.selectedDateRange = convertDateStringsToObjects(content.selectedDateRange);
    }
    if (content.selectedCreationDateRange) {
      content.selectedCreationDateRange = convertDateStringsToObjects(content.selectedCreationDateRange);
    }
  } catch (error) {
    console.error('💥 Error parsing filter set content:', error);
    content = {};
  }

  return {
    ...apiSet,
    content,
  };
}

export const createFilterSetsSlice: StateCreator<FilterSetsSlice, [], [], FilterSetsSlice> = (set, get) => ({
  filterSets: [],
  selectedFilterSet: null,
  lastClickedFilterSet: null,

  setFilterSets: filterSets => set({ filterSets }),

  addFilterSet: filterSet =>
    set(state => ({
      filterSets: [...state.filterSets, filterSet],
    })),

  updateFilterSet: (id, updatedFilterSet) =>
    set(state => ({
      filterSets: state.filterSets.map(filterSet => (filterSet.filterSetId === id ? updatedFilterSet : filterSet)),
    })),

  removeFilterSet: id =>
    set(state => ({
      filterSets: state.filterSets.filter(filterSet => filterSet.filterSetId !== id),
    })),

  getFilterSet: id => get().filterSets.find(filterSet => filterSet.filterSetId === id),

  validateFilterSet: (name, filterValues, excludeId) => {
    const { filterSets } = get();

    if (name.length > 50) {
      return { valid: false, error: 'advanced-filter-error-name-too-long' };
    }

    if (filterSets.length >= 10) {
      return { valid: false, error: 'advanced-filter-error-max-sets-reached' };
    }

    if (
      filterSets.some(
        filterSet => filterSet.filterSetId !== excludeId && filterSet.name.toLowerCase() === name.toLowerCase()
      )
    ) {
      return { valid: false, error: 'advanced-filter-error-name-exists' };
    }

    const changedFilters = FilterSetMatcher.extractChangedFilters(filterValues);

    const existingFilterWithSameValues = filterSets.find(
      filterSet => filterSet.filterSetId !== excludeId && arePartialFiltersEqual(filterSet.content, changedFilters)
    );

    if (existingFilterWithSameValues) {
      return {
        valid: false,
        error: excludeId
          ? `advanced-filter-error-another-same-filters-exist:${existingFilterWithSameValues.name}`
          : `advanced-filter-error-same-filters-exist:${existingFilterWithSameValues.name}`,
      };
    }

    if (Object.keys(changedFilters).length === 0) {
      return {
        valid: false,
        error: 'advanced-filter-error-no-changes',
      };
    }

    return { valid: true };
  },

  setSelectedFilterSet: id => set(() => ({ selectedFilterSet: id, lastClickedFilterSet: id })), // Auto-set lastClicked when setting selected
  setLastClickedFilterSet: id => set(() => ({ lastClickedFilterSet: id })),
});
